// React
import { useMemo, Fragment } from "react"

// Translations
import { useTrans } from "@/i18n"
import { useLocalizedDateTime } from "@/lib/dates"

// UI
import { Heading } from "@/components/Typography"
import { CardBody } from "@/components/Card"
import { FormGroup } from "@/components/form-controls/FormGroup"

// Table
import {
	createColumnHelper,
	getCoreRowModel,
	useReactTable,
	flexRender,
} from "@/lib/table"
import {
	Table,
	TableBody,
	TableDataCell,
	TableHead,
	TableHeading,
	TableRowCell,
} from "@/components/table-controls/TableItems"

// Types
import { OrganisationMembershipType } from "@/api/graphql/types"

// Partial type for memberships that only includes the fields we actually use
type MembershipData = Pick<
	OrganisationMembershipType,
	"user_name" | "user_email" | "role" | "modified_at"
> & {
	id: string
}

interface OrganizationMembersTableProps {
	memberships:
		| readonly (MembershipData | null | undefined)[]
		| null
		| undefined
}

/**
 * OrganizationMembersTable
 * Displays a table of organization members with their roles and join dates
 *
 * @returns
 */
export const OrganizationMembersTable = ({
	memberships,
}: OrganizationMembersTableProps) => {
	// I18n
	const t = useTrans("profile")
	const DateTime = useLocalizedDateTime()

	// Memberships table
	const columnHelper = createColumnHelper<MembershipData>()
	const membershipColumns = useMemo(
		() => [
			columnHelper.accessor("user_name", {
				header: () => (
					<TableHeading variant="static">
						{t("profile:company_details.memberships.user_name")}
					</TableHeading>
				),
				cell: (info) => (
					<TableDataCell>{info.getValue() ?? "-"}</TableDataCell>
				),
			}),
			columnHelper.accessor("user_email", {
				header: () => (
					<TableHeading variant="static">
						{t("profile:company_details.memberships.user_email")}
					</TableHeading>
				),
				cell: (info) => (
					<TableDataCell>{info.getValue() ?? "-"}</TableDataCell>
				),
			}),
			columnHelper.accessor("role", {
				header: () => (
					<TableHeading variant="static">
						{t("profile:company_details.memberships.role")}
					</TableHeading>
				),
				cell: (info) => (
					<TableDataCell>
						{t(
							`profile:company_details.memberships.roles.${info.getValue()}`,
						)}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("modified_at", {
				header: () => (
					<TableHeading variant="static">
						{t("profile:company_details.memberships.modified_at")}
					</TableHeading>
				),
				cell: (info) => (
					<TableDataCell>
						{DateTime.fromISO(info.getValue()).toLocaleString({
							year: "numeric",
							month: "long",
							day: "numeric",
						})}
					</TableDataCell>
				),
			}),
		],
		[t, DateTime.fromISO],
	)

	const table = useReactTable({
		columns: membershipColumns,
		data:
			(memberships?.filter(
				(m): m is MembershipData => m !== null && m !== undefined,
			) as MembershipData[]) ?? [],
		getCoreRowModel: getCoreRowModel(),
	})

	// Don't render if no memberships
	if (!memberships || memberships.length === 0) {
		return null
	}

	return (
		<CardBody className="border-t border-gray-200">
			<Heading as="h2" styleAs="h5" className="mb-3 sm:truncate">
				{t("profile:company_details.memberships.title")}
			</Heading>
			<FormGroup
				heading={<p>{t("profile:company_details.memberships.copy")}</p>}
			>
				<Table>
					<TableHead>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRowCell key={headerGroup.id}>
								{headerGroup.headers.map((header) => (
									<Fragment key={header.id}>
										{flexRender(
											header.column.columnDef.header,
											header.getContext(),
										)}
									</Fragment>
								))}
							</TableRowCell>
						))}
					</TableHead>
					<TableBody>
						{table.getRowModel().rows.map((row, index) => (
							<TableRowCell key={row.id} isOdd={index % 2 === 0}>
								{row.getAllCells().map((cell) => (
									<Fragment key={cell.id}>
										{flexRender(
											cell.column.columnDef.cell,
											cell.getContext(),
										)}
									</Fragment>
								))}
							</TableRowCell>
						))}
					</TableBody>
				</Table>
			</FormGroup>
		</CardBody>
	)
}
